import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Button } from "../components/ui";
import { trackEvent } from "../lib/analytics";
import { assetUrl } from "../lib/assets";
import type { SessionState } from "../types";

interface AppShellProps {
  session: SessionState;
  onLogout: () => void;
}

interface NavItem {
  label: string;
  to: string;
  icon: string;
}

const primaryNav: NavItem[] = [
  { label: "Home", to: "/app/home", icon: "🏠" },
  { label: "Scans", to: "/app/scans", icon: "📸" },
  { label: "Pantry", to: "/app/pantry", icon: "🥫" },
  { label: "Explore", to: "/app/explore", icon: "🔎" },
  { label: "More", to: "/app/more", icon: "⋯" },
];

const dietitianNav: NavItem[] = [
  { label: "Home", to: "/app/home", icon: "🏠" },
  { label: "AI Review", to: "/app/ai-insights/review", icon: "🧠" },
  { label: "Blogs", to: "/app/dietitian-says/mine", icon: "✍️" },
  { label: "Visits", to: "/app/visits", icon: "🗓️" },
  { label: "More", to: "/app/more", icon: "⋯" },
];

function NavButton({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        `flex w-full items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition ${
          isActive
            ? "pr-nav-active"
            : "border-l-2 border-transparent text-[var(--pr-text-muted)] hover:bg-[rgba(164,214,76,0.03)] hover:text-[var(--pr-text)]"
        }`
      }
    >
      <span aria-hidden>{item.icon}</span>
      <span>{item.label}</span>
    </NavLink>
  );
}

export function AppShell({ session, onLogout }: AppShellProps) {
  const location = useLocation();
  const isDietitian = Boolean(session.user?.isDietitian);
  const actualIsDietitian = Boolean(session.user?.actualIsDietitian);
  const canSwitchToDietitian = actualIsDietitian && !isDietitian;
  const [switchingToDietitian, setSwitchingToDietitian] = useState(false);
  const navItems = isDietitian ? dietitianNav : primaryNav;

  function handleSwitchToDietitianView(): void {
    if (switchingToDietitian) {
      return;
    }
    trackEvent("role_switch_requested", { target_role: "dietitian", source: "app_shell" });
    setSwitchingToDietitian(true);
    const baseUrl = session.dietitianLoginUrl || "/app/auth/sign-in/dietitian/";
    const nextPath = `${location.pathname}${location.search}` || "/app/home";
    const params = new URLSearchParams({ next: nextPath });
    const separator = baseUrl.includes("?") ? "&" : "?";
    window.location.href = `${baseUrl}${separator}${params.toString()}`;
  }

  return (
    <div data-testid="app-shell" className="pr-app-shell-bg min-h-screen">
      <div className="mx-auto grid min-h-screen w-full max-w-[1280px] grid-cols-1 gap-3 px-3 py-3 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-4 lg:px-4 lg:py-4">
        <aside className="hidden lg:block">
          <div className="sticky top-4 space-y-3">
            <div className="pr-card p-3">
              <div className="flex items-center gap-3 rounded-sm px-2 py-2">
                <img src={assetUrl("app-icon-logo.png")} alt="PantryRx" className="h-12 w-12 rounded-lg" />
                <div>
                  <p className="pr-display text-[2.05rem] leading-none font-semibold">PantryRx</p>
                </div>
              </div>
              <nav data-testid="app-shell-nav-desktop" className="mt-2 flex flex-col gap-1">
                {navItems.map((item) => (
                  <NavButton key={item.to} item={item} />
                ))}
              </nav>
              {isDietitian ? (
                <div className="mt-3 space-y-1 rounded-sm border-l-2 border-[rgba(149,211,186,0.3)] bg-[var(--pr-surface-container-low)] p-2">
                  <p className="pr-label px-2">Dietitian</p>
                  <NavLink
                    to="/app/dietitian-says/mine"
                    className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm font-medium text-[var(--pr-text-muted)] hover:bg-[rgba(164,214,76,0.03)]"
                  >
                    <span aria-hidden>✍️</span>
                    <span>Author posts</span>
                  </NavLink>
                  <NavLink
                    to="/app/ai-insights/review"
                    className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm font-medium text-[var(--pr-text-muted)] hover:bg-[rgba(164,214,76,0.03)]"
                  >
                    <span aria-hidden>🧠</span>
                    <span>AI review</span>
                  </NavLink>
                  <NavLink
                    to="/app/visits"
                    className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm font-medium text-[var(--pr-text-muted)] hover:bg-[rgba(164,214,76,0.03)]"
                  >
                    <span aria-hidden>🗓️</span>
                    <span>Visit triage</span>
                  </NavLink>
                </div>
              ) : null}
            </div>

            <div className="pr-card p-3">
              <p className="text-sm font-semibold">{session.user?.name}</p>
              <p className="mt-1 truncate text-xs text-[var(--pr-text-muted)]">{session.user?.email}</p>
              <Button className="mt-3 w-full" variant="secondary" onClick={onLogout}>
                Sign out
              </Button>
            </div>
          </div>
        </aside>

        <main className="min-w-0">
          {canSwitchToDietitian ? (
            <div className="mb-3 rounded-sm border-l-2 border-[rgba(164,214,76,0.4)] bg-[rgba(164,214,76,0.06)] px-4 py-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-semibold text-[var(--pr-accent)]">
                  Dietitian privileges detected on your account. Switch views to access dietitian-only workflows.
                </p>
                <Button
                  className="min-h-10 px-4 py-2 text-sm"
                  variant="secondary"
                  disabled={switchingToDietitian}
                  onClick={handleSwitchToDietitianView}
                >
                  {switchingToDietitian ? "Switching..." : "Switch to Dietitian View"}
                </Button>
              </div>
            </div>
          ) : null}
          {isDietitian ? (
            <div className="mb-3 rounded-sm border-l-2 border-[rgba(164,214,76,0.4)] bg-[rgba(164,214,76,0.06)] px-4 py-2.5 text-sm font-semibold text-[var(--pr-accent)]">
              You are viewing the app as a Registered Dietitian
            </div>
          ) : null}
          <Outlet context={{ session }} />
        </main>
      </div>

      <div className="h-[calc(var(--pr-mobile-nav-height)+env(safe-area-inset-bottom))] lg:hidden" aria-hidden />

      <nav
        data-testid="app-shell-nav-mobile"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-[rgba(64,73,68,0.12)] bg-[rgba(25,31,49,0.85)] pb-[env(safe-area-inset-bottom)] shadow-glass backdrop-blur-xl lg:hidden"
      >
        <ul className="mx-auto grid min-h-[var(--pr-mobile-nav-height)] max-w-[640px] grid-cols-5 px-1 py-1.5">
          {navItems.map((item) => {
            const isActive = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={`flex flex-col items-center gap-1 rounded-lg px-2 py-2 text-[11px] font-semibold ${
                    isActive ? "text-[var(--pr-accent)]" : "text-[var(--pr-text-muted)]"
                  }`}
                >
                  <span aria-hidden className="text-base">
                    {item.icon}
                  </span>
                  <span>{item.label}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
